import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Button } from "../ui";
import ConsentCheckbox from "./ConsentCheckbox";
import { LegalDocLink } from "./LegalDocLink";
import { LEGAL_PUBLIC_ENABLED } from "../../constants/legalPaths";
import {
  hasCurrentConsentPd,
  hasCurrentTerms,
  resolveLegalGateMode,
} from "../../utils/legalAcceptance";
import { acceptLegalFetch, updateProfile } from "../../store/authSlice";
import s from "./legalGates.module.css";

/** Блокирующий догон оферты / согласия ПДн для владельца клуба. */
export default function UserLegalGate() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { isAuth, isPlatformAdmin, role, profile } = useSelector(
    (state) => state.auth,
  );
  const mode = resolveLegalGateMode(role, profile);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [consentPd, setConsentPd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setAcceptTerms(hasCurrentTerms(profile));
    setConsentPd(hasCurrentConsentPd(profile));
    setError("");
  }, [profile?.id, profile?.legal_terms_version, profile?.legal_consent_pd_version]);

  const onLegalPage = location.pathname.startsWith("/legal");

  if (
    !LEGAL_PUBLIC_ENABLED ||
    !isAuth ||
    isPlatformAdmin ||
    mode !== "owner_blocking" ||
    onLegalPage
  ) {
    return null;
  }

  const needTerms = !hasCurrentTerms(profile);
  const needPd = !hasCurrentConsentPd(profile);
  const canSubmit =
    (!needTerms || acceptTerms) && (!needPd || consentPd) && !loading;

  const submit = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await dispatch(
        acceptLegalFetch({
          accept_terms: acceptTerms,
          consent_pd: consentPd,
        }),
      ).unwrap();
      if (result?.user) {
        dispatch(updateProfile(result.user));
      }
    } catch (e) {
      setError(
        typeof e === "string"
          ? e
          : e?.message || "Не удалось сохранить согласие",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={s.overlay} role="dialog" aria-modal="true">
      <div className={s.modal}>
        <h2 className={s.title}>Нужно принять условия сервиса</h2>
        <p className={s.lead}>
          Мы обновили юридические документы Бай.Спорт. Чтобы продолжить работу в
          кабинете, подтвердите согласие с актуальной редакцией.
        </p>
        {needTerms ? (
          <ConsentCheckbox
            id="gate-accept-offer"
            checked={acceptTerms}
            onChange={setAcceptTerms}
            disabled={loading}
          >
            Принимаю{" "}
            <LegalDocLink doc="offer" target="_blank" rel="noopener noreferrer">
              Пользовательское соглашение (оферту)
            </LegalDocLink>
            {" и "}
            <LegalDocLink doc="payments" target="_blank" rel="noopener noreferrer">
              Условия платежей
            </LegalDocLink>
          </ConsentCheckbox>
        ) : null}
        {needPd ? (
          <ConsentCheckbox
            id="gate-consent-pd"
            checked={consentPd}
            onChange={setConsentPd}
            disabled={loading}
          >
            Даю согласие на обработку моих персональных данных согласно{" "}
            <LegalDocLink
              doc="ownerConsent"
              target="_blank"
              rel="noopener noreferrer"
            >
              тексту согласия
            </LegalDocLink>
            {" ("}
            <LegalDocLink doc="privacy" target="_blank" rel="noopener noreferrer">
              политика
            </LegalDocLink>
            )          </ConsentCheckbox>
        ) : null}
        {error ? <p className={s.error}>{error}</p> : null}
        <div className={s.actions}>
          <Button type="button" block size="lg" disabled={!canSubmit} onClick={submit}>
            {loading ? "Сохраняем…" : "Продолжить"}
          </Button>
        </div>
      </div>
    </div>
  );
}
